import { SocialNetwork } from 'src/features/auth/data/service/authService.types';
import {
  DocumentNumberValidation,
  DocumentTypeValidation,
} from 'src/features/profile/presentation/components/UserVerificationDialog/validation';
import * as Yup from 'yup';

const PHONE_REGEX =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{4,10}$/;

const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;

const EmailValidation = Yup.string()
  .matches(EMAIL_REGEX, 'El correo electrónico no es válido.')
  .required('El correo electrónico es obligatorio.');

const PhoneValidation = Yup.string()
  .required('El número de teléfono es obligatorio.')
  .matches(PHONE_REGEX, 'El número de teléfono no es válido.');

const NameValidation = Yup.string()
  .trim()
  .required('El nombre es obligatorio.')
  .min(2, 'El nombre debe tener al menos 2 caracteres.')
  .max(35, 'El nombre no puede exceder 60 caracteres.')
  .test(
    'no-multiple-spaces',
    'No usar más de un espacio consecutivo.',
    (value) => !/ {2,}/.test(value || '')
  )
  .matches(
    /^[\p{L}]+(?: [\p{L}]+)*$/u,
    'El nombre solo puede contener letras y espacios simples.'
  );

const LastNameValidation = Yup.string()
  .trim()
  .required('El apellido es obligatorio.')
  .min(2, 'El apellido debe tener al menos 2 caracteres.')
  .max(35, 'El apellido no puede exceder 60 caracteres.')
  .test(
    'no-multiple-spaces',
    'No usar más de un espacio consecutivo.',
    (value) => !/ {2,}/.test(value || '')
  )
  .matches(
    /^[\p{L}]+(?: [\p{L}]+)*$/u,
    'El apellido solo puede contener letras y espacios simples.'
  );

const BasicFields = {
  email: EmailValidation,
  first_name: NameValidation,
  last_name: LastNameValidation,
  phone_number: PhoneValidation,
  birth_date: Yup.string()
    .required('La fecha de nacimiento es obligatoria.')
    .test('DOB', 'Debes ser mayor de 18 años', function (value) {
      const date = new Date(value);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const age = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
      return age >= 18;
    }),
  t_and_c: Yup.boolean()
    .oneOf([true], 'Debes aceptar los términos y condiciones.')
    .required('Debes aceptar antes de continuar'),
};

const RegistrationValidationSchemaWithDocument = Yup.object().shape({
  ...BasicFields,
  password: Yup.string()
    .min(8, 'Mínimo 8 caracteres')
    .required('La contraseña es obligatoria.'),
  password_confirmation: Yup.string().oneOf(
    [Yup.ref('password'), undefined],
    'Las contraseñas no coinciden.'
  ),
  document_type: DocumentTypeValidation,
  document_number: DocumentNumberValidation,
});

const RegistrationValidationSchemaWithoutDocument = Yup.object().shape({
  ...BasicFields,
  password: Yup.string()
    .min(8, 'Mínimo 8 caracteres')
    .required('La contraseña es obligatoria.'),
  password_confirmation: Yup.string().oneOf(
    [Yup.ref('password'), undefined],
    'Las contraseñas no coinciden.'
  ),
});

const GoogleRegistrationValidationSchemaWithDocument = Yup.object().shape({
  ...BasicFields,
  document_type: DocumentTypeValidation,
  document_number: DocumentNumberValidation,
});

const GoogleRegistrationValidationSchemaWithoutDocument = Yup.object().shape({
  ...BasicFields,
});

export const RegistrationValidationSchema = {
  withDocument: RegistrationValidationSchemaWithDocument,
  withoutDocument: RegistrationValidationSchemaWithoutDocument,
};

export const GoogleRegistrationValidationSchema = {
  withDocument: GoogleRegistrationValidationSchemaWithDocument,
  withoutDocument: GoogleRegistrationValidationSchemaWithoutDocument,
};

export const SignInValidationSchema = Yup.object().shape({
  email: EmailValidation,
  password: Yup.string().required('La contraseña es obligatoria.'),
});

export const PasswordResetValidationSchema = Yup.object().shape({
  email: EmailValidation,
});

export const SignUpValidationSchemaWithoutDocument = Yup.object().shape({
  email: EmailValidation,
  first_name: NameValidation,
  last_name: LastNameValidation,
  phone_number: PhoneValidation,
  birth_date: Yup.string()
    .required('La fecha de nacimiento es obligatoria.')
    .test('DOB', 'Debes ser mayor de 18 años', function (value) {
      const date = new Date(value);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const age = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
      return age >= 18;
    }),
  t_and_c: Yup.boolean()
    .oneOf([true], 'Debes aceptar los términos y condiciones.')
    .required('Debes aceptar antes de continuar'),
  social_network: Yup.object().notRequired(),
  password: Yup.string().when('social_network', {
    is: (value: SocialNetwork) => value !== undefined,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .required('Ingresa tu contraseña')
        .min(8, 'Mínimo 8 caracteres')
        .matches(
          /^\S.*$/,
          'La contraseña no puede comenzar con espacios vacíos.'
        )
        .matches(
          /.*\S$/,
          'La contraseña no puede terminar con espacios vacíos.'
        ),
  }),
  password_confirmation: Yup.string().when('social_network', {
    is: (value: SocialNetwork) => value !== undefined,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.oneOf(
        [Yup.ref('password'), undefined],
        'Las contraseñas no coinciden.'
      ),
  }),
});

const SignUpValidationSchemaWithDocument = Yup.object().shape({
  ...BasicFields,
  document_type: DocumentTypeValidation,
  document_number: DocumentNumberValidation,
});

export const SignUpValidationSchema = {
  withDocument: SignUpValidationSchemaWithDocument,
  withoutDocument: SignUpValidationSchemaWithoutDocument,
};

export const VerificationSchema = Yup.object().shape({
  email: EmailValidation,
  phone_number: PhoneValidation,
});
